import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAccount } from "../../context/AccountContext";

const STATUS_COLOR = {
  ACTIVE:    { bg: "#e8f4fd", color: "#0070c9", border: "#b3d7f5" },
  TRIGGERED: { bg: "#eafaf1", color: "#1a7a40", border: "#a3dfc0" },
  DISABLED:  { bg: "#f5f5f5", color: "#888",    border: "#ddd" },
};

const EMPTY_FORM = { symbolname: "", direction: "above", target_price: "", note: "" };

/* ── Symbol autocomplete component ──────────────────────────────── */
function SymbolInput({ value, onChange, onValidChange, username, allSymbols, isUS }) {
  const [query, setQuery]         = useState(value || "");
  const [suggestions, setSugs]    = useState([]);
  const [showDrop, setShowDrop]   = useState(false);
  const [error, setError]         = useState("");
  const [usFetching, setUsFetch]  = useState(false);
  const debounceRef               = useRef(null);
  const wrapRef                   = useRef(null);

  // Keep query in sync when parent resets form
  useEffect(() => { setQuery(value || ""); setError(""); }, [value]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setShowDrop(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleInput = (raw) => {
    const val = raw.toUpperCase();
    setQuery(val);
    setError("");
    onChange(val);        // pass raw input up (may be invalid)
    onValidChange("");    // clear valid until selected

    if (!val) { setSugs([]); setShowDrop(false); return; }

    if (!isUS) {
      // Client-side filter on preloaded list
      const matches = allSymbols.filter(s => s.startsWith(val)).slice(0, 10);
      setSugs(matches);
      setShowDrop(matches.length > 0);
    } else {
      // Debounce Yahoo Finance proxy
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(async () => {
        if (val.length < 2) return;
        setUsFetch(true);
        try {
          const res = await axios.get("/alerts/symbols", { params: { q: val, username } });
          setSugs(res.data || []);
          setShowDrop((res.data || []).length > 0);
        } catch { setSugs([]); }
        finally { setUsFetch(false); }
      }, 300);
    }
  };

  const select = (sym) => {
    setQuery(sym);
    setError("");
    onChange(sym);
    onValidChange(sym);   // mark as validated
    setSugs([]);
    setShowDrop(false);
  };

  const validate = () => {
    if (!query) return;
    if (!isUS) {
      if (!allSymbols.includes(query)) {
        setError(`"${query}" is not in the traded symbol list.`);
        onValidChange("");
      } else {
        onValidChange(query);
      }
    }
    // For US we accept whatever Yahoo returned; if it was selected from dropdown it's valid
  };

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <input
        style={{ ...inputStyle, borderColor: error ? "#dc3545" : "#ccc" }}
        placeholder={isUS ? "e.g. AAPL" : "e.g. RELIANCE"}
        value={query}
        onChange={e => handleInput(e.target.value)}
        onFocus={() => {
          if (!isUS && query.length === 0) {
            setSugs(allSymbols.slice(0, 10));
            setShowDrop(true);
          }
        }}
        onBlur={validate}
        autoComplete="off"
      />
      {usFetching && (
        <span style={{ position: "absolute", right: 8, top: 9, fontSize: 11, color: "#888" }}>…</span>
      )}
      {showDrop && suggestions.length > 0 && (
        <ul style={dropStyle}>
          {suggestions.map(s => (
            <li
              key={s}
              onMouseDown={() => select(s)}
              style={dropItem}
              onMouseEnter={e => e.currentTarget.style.background = "#f0f7ff"}
              onMouseLeave={e => e.currentTarget.style.background = "#fff"}
            >
              {s}
            </li>
          ))}
        </ul>
      )}
      {error && <div style={{ color: "#dc3545", fontSize: 11, marginTop: 3 }}>{error}</div>}
    </div>
  );
}

/* ── Main page ───────────────────────────────────────────────────── */
export default function PriceAlertsPage() {
  const { selectedAccount } = useAccount();
  const [alerts, setAlerts]         = useState([]);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [loading, setLoading]       = useState(false);
  const [showForm, setShowForm]     = useState(false);
  const [form, setForm]             = useState(EMPTY_FORM);
  const [validSymbol, setValidSym]  = useState("");   // validated symbol value
  const [saving, setSaving]         = useState(false);
  const [editId, setEditId]         = useState(null);
  const [msg, setMsg]               = useState(null);
  const [search, setSearch]         = useState("");
  const [allSymbols, setAllSymbols] = useState([]);   // preloaded for IN market
  const [isUS, setIsUS]             = useState(false);

  const flash = (text, ok = true) => {
    setMsg({ text, ok });
    setTimeout(() => setMsg(null), 3000);
  };

  // Load symbol list and market type once per account
  useEffect(() => {
    if (!selectedAccount) return;
    axios.get("/alerts/symbols", { params: { username: selectedAccount } })
      .then(res => {
        const data = res.data;
        if (Array.isArray(data)) {
          setAllSymbols(data);
          setIsUS(false);
        } else {
          // US returns error or empty — flag as US mode
          setAllSymbols([]);
          setIsUS(true);
        }
      })
      .catch(() => { setAllSymbols([]); setIsUS(true); });
  }, [selectedAccount]);

  // Detect US: if symbols list is small (<5) it's likely a failed IN lookup → treat as US
  // Actually, re-detect from account market field via a dedicated call is cleaner
  useEffect(() => {
    if (!selectedAccount) return;
    // Check market by passing no query — if response is array it's IN, otherwise US
    axios.get("/alerts/symbols", { params: { username: selectedAccount, q: "" } })
      .then(res => {
        if (Array.isArray(res.data)) {
          setAllSymbols(res.data);
          setIsUS(false);
        }
      }).catch(() => setIsUS(true));
  }, [selectedAccount]);

  const fetchAlerts = useCallback(async () => {
    if (!selectedAccount) return;
    setLoading(true);
    try {
      const params = { username: selectedAccount };
      if (statusFilter !== "ALL") params.status = statusFilter;
      const res = await axios.get("/alerts/price", { params });
      setAlerts(res.data || []);
    } catch {
      flash("Failed to load alerts.", false);
    } finally {
      setLoading(false);
    }
  }, [selectedAccount, statusFilter]);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const openNew = () => {
    setEditId(null);
    setForm(EMPTY_FORM);
    setValidSym("");
    setShowForm(true);
  };

  const openEdit = (alert) => {
    setEditId(alert.id);
    setForm({
      symbolname:   alert.symbolname,
      direction:    alert.direction,
      target_price: alert.target_price,
      note:         alert.note || "",
    });
    setValidSym(alert.symbolname); // already stored = valid
    setShowForm(true);
  };

  const handleSave = async () => {
    const symToSave = editId ? form.symbolname : validSymbol;
    if (!symToSave) {
      flash("Please select a valid symbol from the list.", false);
      return;
    }
    if (!form.target_price) {
      flash("Target price is required.", false);
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, symbolname: symToSave };
      if (editId) {
        await axios.put(`/alerts/price/${editId}`, payload);
        flash("Alert updated.");
      } else {
        await axios.post("/alerts/price", { ...payload, username: selectedAccount });
        flash("Alert created.");
      }
      setShowForm(false);
      fetchAlerts();
    } catch {
      flash("Failed to save alert.", false);
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      await axios.put(`/alerts/price/${id}`, { status });
      fetchAlerts();
    } catch {
      flash("Failed to update status.", false);
    }
  };

  const deleteAlert = async (id) => {
    if (!window.confirm("Delete this alert?")) return;
    try {
      await axios.delete(`/alerts/price/${id}`);
      fetchAlerts();
    } catch {
      flash("Failed to delete.", false);
    }
  };

  const displayed = alerts
    .filter(a => statusFilter === "ALL" || a.status === statusFilter)
    .filter(a => !search || a.symbolname.includes(search.toUpperCase()))
    .sort((a, b) => a.symbolname.localeCompare(b.symbolname));

  return (
    <div style={{ padding: 25, background: "#f4f6f9", minHeight: "100vh" }}>

      {msg && (
        <div style={{
          position: "fixed", top: 20, right: 20, zIndex: 9999,
          padding: "10px 20px", borderRadius: 8, color: "#fff", fontWeight: 600,
          background: msg.ok ? "#28a745" : "#dc3545"
        }}>
          {msg.text}
        </div>
      )}

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#2c3e50" }}>Price Alerts</h2>
          <p style={{ margin: "4px 0 0", color: "#888", fontSize: 13 }}>
            Get notified when a stock crosses your target price.
            {isUS && <span style={{ marginLeft: 8, background: "#e8f4fd", color: "#0070c9", padding: "1px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600 }}>US Market</span>}
            {!isUS && <span style={{ marginLeft: 8, background: "#fff3cd", color: "#856404", padding: "1px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600 }}>IN Market</span>}
          </p>
        </div>
        <button style={btnPrimary} onClick={openNew}>+ New Alert</button>
      </div>

      {/* Filter bar: status pills + search box */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
        {["ALL", "ACTIVE", "TRIGGERED", "DISABLED"].map(s => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            style={{
              padding: "6px 14px", borderRadius: 20, border: "1px solid",
              cursor: "pointer", fontSize: 12, fontWeight: 600,
              background: statusFilter === s ? "#007bff" : "#fff",
              color:      statusFilter === s ? "#fff"    : "#555",
              borderColor: statusFilter === s ? "#007bff" : "#ccc",
            }}
          >
            {s}
          </button>
        ))}
        <div style={{ marginLeft: "auto", position: "relative" }}>
          <span style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", color: "#aaa", fontSize: 13, pointerEvents: "none" }}>&#128269;</span>
          <input
            type="text"
            placeholder="Search symbol..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ paddingLeft: 28, paddingRight: 10, paddingTop: 6, paddingBottom: 6, borderRadius: 20, border: "1px solid #ccc", fontSize: 13, width: 180, outline: "none" }}
          />
          {search && (
            <span
              onClick={() => setSearch("")}
              style={{ position: "absolute", right: 9, top: "50%", transform: "translateY(-50%)", color: "#aaa", cursor: "pointer", fontSize: 14, fontWeight: 700 }}
            >
              &times;
            </span>
          )}
        </div>
      </div>

      {/* New / Edit form */}
      {showForm && (
        <div style={formCard}>
          <h5 style={{ margin: "0 0 16px", color: "#2c3e50" }}>
            {editId ? "Edit Alert" : "New Price Alert"}
          </h5>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 2fr", gap: 12, alignItems: "start" }}>
            <div>
              <label style={fieldLabel}>Symbol</label>
              {editId ? (
                // Symbol is locked when editing
                <input style={{ ...inputStyle, background: "#f5f5f5" }} value={form.symbolname} disabled />
              ) : (
                <SymbolInput
                  value={form.symbolname}
                  onChange={val => setForm(f => ({ ...f, symbolname: val }))}
                  onValidChange={val => setValidSym(val)}
                  username={selectedAccount}
                  allSymbols={allSymbols}
                  isUS={isUS}
                />
              )}
            </div>
            <div>
              <label style={fieldLabel}>Condition</label>
              <select
                style={inputStyle}
                value={form.direction}
                onChange={e => setForm(f => ({ ...f, direction: e.target.value }))}
              >
                <option value="above">Price rises above</option>
                <option value="below">Price falls below</option>
              </select>
            </div>
            <div>
              <label style={fieldLabel}>Target Price</label>
              <input
                style={inputStyle}
                type="number"
                step="0.01"
                placeholder="0.00"
                value={form.target_price}
                onChange={e => setForm(f => ({ ...f, target_price: e.target.value }))}
              />
            </div>
            <div>
              <label style={fieldLabel}>Note (optional)</label>
              <input
                style={inputStyle}
                placeholder="e.g. Breakout level"
                value={form.note}
                onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
              />
            </div>
          </div>
          <div style={{ marginTop: 14, display: "flex", gap: 10 }}>
            <button style={btnPrimary} onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : editId ? "Update Alert" : "Create Alert"}
            </button>
            <button style={btnSecondary} onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Alerts table */}
      {loading ? (
        <p style={{ color: "#888" }}>Loading...</p>
      ) : displayed.length === 0 ? (
        <div style={emptyState}>
          <p style={{ margin: 0, fontSize: 14 }}>No alerts found.</p>
          <p style={{ margin: "6px 0 0", fontSize: 12, color: "#aaa" }}>
            Click "New Alert" to create your first price alert.
          </p>
        </div>
      ) : (
        <div style={tableWrap}>
          <table style={table}>
            <thead>
              <tr style={theadRow}>
                <th style={th}>Symbol</th>
                <th style={th}>Condition</th>
                <th style={th}>Target</th>
                <th style={th}>Last Price</th>
                <th style={th}>Status</th>
                <th style={th}>Note</th>
                <th style={th}>Triggered At</th>
                <th style={th}>Created</th>
                <th style={th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayed.map(a => {
                const sc = STATUS_COLOR[a.status] || STATUS_COLOR.DISABLED;
                return (
                  <tr key={a.id} style={tr}>
                    <td style={{ ...td, fontWeight: 700 }}>
                      <Link
                        to={`/trading/${a.symbolname}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#2c3e50", textDecoration: "underline" }}
                      >
                        {a.symbolname}
                      </Link>
                    </td>
                    <td style={td}>
                      <span style={{
                        padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600,
                        background: a.direction === "above" ? "#fff3cd" : "#fde8e8",
                        color:      a.direction === "above" ? "#856404" : "#c0392b",
                      }}>
                        {a.direction === "above" ? "▲ above" : "▼ below"}
                      </span>
                    </td>
                    <td style={{ ...td, fontWeight: 600 }}>{Number(a.target_price).toLocaleString()}</td>
                    <td style={{ ...td, color: "#555" }}>
                      {a.last_checked_price != null ? Number(a.last_checked_price).toLocaleString() : "—"}
                    </td>
                    <td style={td}>
                      <span style={{
                        padding: "3px 10px", borderRadius: 12, fontSize: 11, fontWeight: 700,
                        background: sc.bg, color: sc.color, border: `1px solid ${sc.border}`
                      }}>
                        {a.status}
                      </span>
                    </td>
                    <td style={{ ...td, color: "#777", fontSize: 12 }}>{a.note || "—"}</td>
                    <td style={{ ...td, fontSize: 12, color: "#888" }}>
                      {a.triggered_at ? a.triggered_at.slice(0, 16) : "—"}
                    </td>
                    <td style={{ ...td, fontSize: 12, color: "#888" }}>
                      {a.created_at ? a.created_at.slice(0, 10) : "—"}
                    </td>
                    <td style={td}>
                      <div style={{ display: "flex", gap: 6 }}>
                        {a.status !== "DISABLED" && (
                          <button style={actionBtn} onClick={() => openEdit(a)}>Edit</button>
                        )}
                        {a.status === "DISABLED" && (
                          <button style={{ ...actionBtn, color: "#28a745", borderColor: "#28a745" }}
                            onClick={() => setStatus(a.id, "ACTIVE")}>Enable</button>
                        )}
                        {a.status === "TRIGGERED" && (
                          <button style={{ ...actionBtn, color: "#0070c9", borderColor: "#0070c9" }}
                            onClick={() => setStatus(a.id, "ACTIVE")}>Reset</button>
                        )}
                        {a.status === "ACTIVE" && (
                          <button style={{ ...actionBtn, color: "#888", borderColor: "#ccc" }}
                            onClick={() => setStatus(a.id, "DISABLED")}>Disable</button>
                        )}
                        <button style={{ ...actionBtn, color: "#dc3545", borderColor: "#dc3545" }}
                          onClick={() => deleteAlert(a.id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ── Styles ── */
const btnPrimary   = { padding: "8px 18px", borderRadius: 8, border: "none", background: "#007bff", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600 };
const btnSecondary = { padding: "8px 18px", borderRadius: 8, border: "1px solid #ccc", background: "#fff", color: "#333", cursor: "pointer", fontSize: 13 };
const actionBtn    = { padding: "4px 10px", borderRadius: 6, border: "1px solid #ccc", background: "#fff", cursor: "pointer", fontSize: 11, fontWeight: 600, color: "#555" };
const formCard     = { background: "#fff", borderRadius: 12, padding: 20, marginBottom: 20, boxShadow: "0 2px 10px rgba(0,0,0,0.07)" };
const tableWrap    = { background: "#fff", borderRadius: 12, overflow: "hidden", boxShadow: "0 2px 10px rgba(0,0,0,0.06)" };
const table        = { width: "100%", borderCollapse: "collapse" };
const theadRow     = { background: "#f8f9fa", borderBottom: "2px solid #e9ecef" };
const th           = { padding: "12px 14px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#6c757d", textTransform: "uppercase", letterSpacing: "0.5px" };
const tr           = { borderBottom: "1px solid #f0f0f0" };
const td           = { padding: "12px 14px", fontSize: 13 };
const inputStyle   = { padding: "7px 10px", borderRadius: 6, border: "1px solid #ccc", fontSize: 13, width: "100%", boxSizing: "border-box" };
const fieldLabel   = { fontSize: 11, fontWeight: 600, color: "#6c757d", display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.4px" };
const emptyState   = { background: "#fff", borderRadius: 12, padding: "40px 20px", textAlign: "center", color: "#888", boxShadow: "0 2px 10px rgba(0,0,0,0.06)" };
const dropStyle    = { position: "absolute", top: "100%", left: 0, right: 0, zIndex: 1000, background: "#fff", border: "1px solid #ccc", borderRadius: 6, margin: 0, padding: 0, listStyle: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.12)", maxHeight: 220, overflowY: "auto" };
const dropItem     = { padding: "8px 12px", cursor: "pointer", fontSize: 13, background: "#fff" };
